/**
 * What the scene knows that the snapshot does not: the deck, and the payoff.
 *
 * Both are read off the steps the `FlightBus` announces and the presented table, so
 * neither needed a change to the Director (whose timeline is not edited for looks):
 *
 *   - THE DECK. Five backs at the dealer's spot from the shuffle, riffling while the
 *     `shuffle` step runs; a two-card stub once the last hole card has left, for the
 *     rest of the hand (the muck lands on it, the board comes from it); nothing before
 *     the first hand.
 *   - THE PAYOFF. At the pause after the last reveal (or the river of a run-out) the
 *     shown hands and the board say who won and with what; at the first push of an
 *     uncontested pot the one seat left standing takes it. `derivePayoff` does the
 *     reading; this hook decides WHEN to read, and clears it at the next shuffle.
 *
 * Shared by the route and the harness so the two cannot drift.
 */
import { useEffect, useRef, useState } from "react";
import type { DirectorSnapshot } from "./director";
import type { FlightBus } from "./flight";
import { derivePayoff, type Payoff } from "./payoff";
import type { Durations } from "./pace";

export type DeckState = "none" | "shuffling" | "full" | "stub";

export interface Scene {
  deck: DeckState;
  payoff: Payoff | null;
  /** The pot's chips are in the air: the static pile under the pill hides until they land. */
  potFlying: boolean;
  /** Bets are converging on the pot: the pile under the pill keeps its old size until they arrive. */
  potFrozen: boolean;
}

export function useScene(bus: FlightBus, snap: DirectorSnapshot | null, durations: Durations): Scene {
  const [deck, setDeck] = useState<DeckState>("none");
  const [payoff, setPayoff] = useState<Payoff | null>(null);
  const [potFlying, setPotFlying] = useState(false);
  const [potFrozen, setPotFrozen] = useState(false);
  const dur = useRef(durations);
  dur.current = durations;

  useEffect(() => {
    let timer: number | null = null;
    let freeze: number | null = null;
    const off = bus.subscribe((step, before) => {
      switch (step.kind) {
        case "collect": {
          /* The pile grows when the last pile has landed, not when the step lands. */
          setPotFrozen(true);
          if (freeze) window.clearTimeout(freeze);
          const last = dur.current.potCollect + dur.current.collectStagger * Math.max(0, step.seats.length - 1);
          freeze = window.setTimeout(() => setPotFrozen(false), last);
          break;
        }
        case "shuffle":
          setPayoff(null);
          setPotFlying(false);
          setDeck("shuffling");
          if (timer) window.clearTimeout(timer);
          timer = window.setTimeout(() => setDeck("full"), dur.current.shuffle);
          break;
        case "pause": {
          /* The breath before the pot moves — if the hands are up, that is the payoff. */
          const p = derivePayoff(before);
          if (p && !p.uncontested) setPayoff(p);
          break;
        }
        case "push": {
          const p = derivePayoff(before);
          if (p && p.uncontested) setPayoff(p);
          setPotFlying(true);
          break;
        }
        default:
          break;
      }
    });
    return () => { off(); if (timer) window.clearTimeout(timer); if (freeze) window.clearTimeout(freeze); };
  }, [bus]);

  /* The stub: every live seat has both cards, so the deal is over. */
  const state = snap?.state ?? null;
  useEffect(() => {
    if (!state || !snap?.dealt) return;
    const live = state.seats.filter((s) => !s.sittingOut).length;
    const dealt = Object.values(snap.dealt).reduce((a, b) => a + b, 0);
    if (live > 0 && dealt >= live * 2) setDeck((d) => (d === "full" ? "stub" : d));
  }, [state, snap?.dealt]);

  /* The pot has landed (it is empty, or the hand is over): the pile may show again. */
  useEffect(() => {
    if (potFlying && state && state.pot <= 0) setPotFlying(false);
  }, [state, potFlying]);

  return { deck, payoff, potFlying, potFrozen };
}
